import React from 'react';
import { RezervacijaAlarm, RezervacijaRadnja, RezervacijaStavka, RezervacijePregled } from '../types';

interface Props {
  pregled: RezervacijePregled | null;
  /**
   * Poslovođin pogled: samo njegov objekat. Ne može „Skinuto sa naloga" — to radi
   * magacin u ERP-u — ali može da potvrdi „Jeste taj kupac".
   */
  zaObjekat?: boolean;
  onRadnja: (id: number, radnja: RezervacijaRadnja) => void;
  zauzeto?: boolean;
}

const KAKO_ZATVORENO: Record<string, string> = {
  SkinutoUErpu: 'skinuto sa naloga u ERP-u',
  SkinutoRukom: 'skinuto sa naloga (potvrđeno rukom)',
  NijeTajKupac: 'nije taj kupac — roba ostaje odvojena'
};

/**
 * Roba odvojena za kupca pre računa (magacin 008) i alarmi kad se proda.
 *
 * Black November, 19.10.2026: šef magacina na molbu poslovođe odvaja robu na
 * jedan nalog po objektu („ZA KULU REZERVACIJA"). Kad kupac kupi, a trgovac
 * upiše da roba ide iz magacina, aplikacija kao i za svaki račun uzme komad iz
 * 021 — a odvojeni ostaje u 008. Ovde se vidi da je prodata baš odvojena roba,
 * i šta treba uraditi: skinuti je sa naloga (vratiti iz 008 u 021) i kupcu dati
 * odvojeni komad. Isti prikaz koriste magacin i poslovođa.
 */
export default function Rezervacije({ pregled, zaObjekat = false, onRadnja, zauzeto = false }: Props) {
  if (!pregled) {
    return <p className="muted">Učitavam…</p>;
  }

  const otvoreni = pregled.alarmi.filter((a) => !a.zatvoren);
  const zatvoreni = pregled.alarmi.filter((a) => a.zatvoren);

  return (
    <div className="rezervacije">
      {pregled.stavkeGreska && <div className="health health--bad">{pregled.stavkeGreska}</div>}

      {otvoreni.length === 0 ? (
        <p className="muted">Nema prodate robe sa naloga rezervacije koja čeka proveru.</p>
      ) : (
        <ul className="rezervacije__alarmi">
          {otvoreni.map((a) => (
            <Alarm
              key={a.id}
              alarm={a}
              pregled={pregled}
              zaObjekat={zaObjekat}
              onRadnja={onRadnja}
              zauzeto={zauzeto}
            />
          ))}
        </ul>
      )}

      <h3 className="rezervacije__naslov">
        {zaObjekat ? 'Šta stoji na nalogu tvog objekta' : 'Šta stoji na nalozima'} u {pregled.magacin}
      </h3>
      {pregled.stavke === null ? (
        <p className="muted">Ne zna se — ERP se trenutno ne čita.</p>
      ) : pregled.stavke.length === 0 ? (
        <p className="muted">Ništa nije odvojeno.</p>
      ) : (
        <Stavke stavke={pregled.stavke} zaObjekat={zaObjekat} />
      )}

      {zatvoreni.length > 0 && (
        <details className="rezervacije__zatvoreni">
          <summary>Zatvoreno u poslednjih 7 dana ({zatvoreni.length})</summary>
          <ul className="storelist">
            {zatvoreni.map((a) => (
              <li key={a.id}>
                <span className="storelist__where">
                  {a.naziv ?? a.artikal} × {a.kolicina}
                  <span className="muted"> · račun {a.racun}{a.kupac ? ` · ${a.kupac}` : ''}</span>
                </span>
                <span className="storelist__why muted">
                  {KAKO_ZATVORENO[a.zatvaranje ?? ''] ?? 'zatvoreno'}
                  {a.zatvorio ? ` — ${a.zatvorio}` : ''}
                  {a.zatvoren ? `, ${new Date(a.zatvoren).toLocaleString('sr-RS')}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Alarm({ alarm, pregled, zaObjekat, onRadnja, zauzeto }: {
  alarm: RezervacijaAlarm;
  pregled: RezervacijePregled;
  zaObjekat: boolean;
  onRadnja: (id: number, radnja: RezervacijaRadnja) => void;
  zauzeto: boolean;
}) {
  const stoji = alarm.naNaloguSada ?? alarm.naNaloguKadJeOtvoren;

  return (
    <li className="rezervacija">
      <p className="rezervacija__sta-je">
        <strong>Prodata rezervisana roba:</strong> {alarm.naziv ?? alarm.artikal} × {alarm.kolicina}
        <span className="muted"> (šifra {alarm.artikal})</span>
      </p>
      <p>
        Račun {alarm.racun}{alarm.kupac ? `, kupac ${alarm.kupac}` : ''}
        {!zaObjekat && <> — prodao {alarm.objekatNaziv ?? 'objekat'} ({alarm.objekat})</>}
        <span className="muted"> · {new Date(alarm.otvoren).toLocaleString('sr-RS')}</span>
      </p>
      <p>
        Stoji na nalogu {alarm.nalog}{alarm.opisNaloga ? ` „${alarm.opisNaloga}"` : ''}:{' '}
        <strong>{stoji} kom</strong>
        {alarm.naNaloguSada === null && <span className="muted"> (stanje pre provere; ERP se sad ne čita)</span>}
        {!alarm.zaObjekat && <span className="muted"> · nalog ne kaže objekat — vidi ga samo magacin</span>}
      </p>
      {alarm.kupacPotvrdjen && (
        <p className="rezervacija__potvrda">
          Poslovođa{alarm.kupcaPotvrdio ? ` (${alarm.kupcaPotvrdio})` : ''} potvrdio: jeste taj kupac.
        </p>
      )}
      <p className="rezervacija__uputstvo">
        {zaObjekat
          ? 'Proveri da li je ovo kupac za kog je roba odvojena. Ako jeste, potvrdi — magacin '
            + 'skida robu sa naloga i kupcu daje odvojeni komad.'
          : `Ako je to taj kupac: skini sa naloga (vrati iz ${pregled.magacin} u ${pregled.izMagacina}) `
            + 'i kupcu daj odvojeni komad. Alarm se zatvori sam kad stavka nestane sa naloga.'}
      </p>
      <div className="rezervacija__dugmad">
        {zaObjekat ? (
          !alarm.kupacPotvrdjen && (
            <button type="button" className="primary" disabled={zauzeto}
              onClick={() => onRadnja(alarm.id, 'jeste-taj-kupac')}>
              Jeste taj kupac
            </button>
          )
        ) : (
          <button type="button" className="primary" disabled={zauzeto}
            onClick={() => onRadnja(alarm.id, 'skinuto')}>
            Skinuto sa naloga
          </button>
        )}
        <button type="button" disabled={zauzeto} onClick={() => onRadnja(alarm.id, 'nije-taj-kupac')}>
          Nije taj kupac
        </button>
      </div>
    </li>
  );
}

/** Stavke po objektu i nalogu, kako ih šef vidi u ERP-u. */
function Stavke({ stavke, zaObjekat }: { stavke: RezervacijaStavka[]; zaObjekat: boolean }) {
  const grupe = new Map<string, RezervacijaStavka[]>();

  for (const s of stavke) {
    const kljuc = `${s.objekat ?? ''}|${s.nalog}`;
    grupe.set(kljuc, [...(grupe.get(kljuc) ?? []), s]);
  }

  return (
    <ul className="storelist rezervacije__stavke">
      {Array.from(grupe.values()).map((grupa) => {
        const prva = grupa[0];
        return (
          <li key={`${prva.objekat ?? ''}|${prva.nalog}`}>
            <span className="storelist__where">
              {!zaObjekat && (
                <strong>{prva.objekat ? `${prva.objekatNaziv ?? 'objekat'} (${prva.objekat})` : 'bez objekta'} · </strong>
              )}
              nalog {prva.nalog}{prva.opisNaloga ? ` „${prva.opisNaloga}"` : ''}
              {prva.datum && <span className="muted"> · od {new Date(prva.datum).toLocaleDateString('sr-RS')}</span>}
            </span>
            <span className="storelist__why">
              {grupa.map((s) => `${s.naziv ?? s.artikal} × ${s.naNalogu}`).join(', ')}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
